import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type DepartmentItem = { id: string; name: string };

export function PositionsFilter() {
  const nav = useNavigate({ from: "/app/positions" });
  const search = useSearch({ from: "/app/positions" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const titleFilter = findCondition(filters, "title");
  const statusFilter = findCondition(filters, "status");
  const departmentFilter = findCondition(filters, "departmentId");
  const levelFilter = findCondition(filters, "level");

  const [searchValue, setSearchValue] = useState<string>(
    (titleFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((titleFilter?.value as string) || ""),
    [titleFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "title",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "title");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const statusValue =
    typeof statusFilter?.value === "boolean" ? String(statusFilter.value) : "";

  const departmentValue = (departmentFilter?.value as string) || "";

  const levelValue =
    typeof levelFilter?.value === "number" ? String(levelFilter.value) : "";

  const hasFilter =
    !!titleFilter?.value || !!statusValue || !!departmentValue || !!levelValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາຕໍາແໜ່ງ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <InfiniteCombobox<DepartmentItem>
            queryKey={["departments", "filter"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={departmentValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "departmentId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "departmentId");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ເລືອກພະແນກ"
            className="h-8 w-full sm:w-40"
            clearable
          />
          <SimpleSelect
            value={levelValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "level");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "level",
                  op: "eq",
                  value: Number.parseInt(val, 10),
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ລະດັບ"
            className="h-8 w-full sm:w-24"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "1", label: "1" },
              { value: "2", label: "2" },
              { value: "3", label: "3" },
              { value: "4", label: "4" },
              { value: "5", label: "5" },
              { value: "6", label: "6" },
              { value: "7", label: "7" },
              { value: "8", label: "8" },
              { value: "9", label: "9" },
              { value: "10", label: "10" },
            ]}
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val === "true",
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "true", label: "ໃຊ້ງານ" },
              { value: "false", label: "ບໍ່ໃຊ້ງານ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
